import React, { useEffect } from 'react';
import { AccountApiResponse, CommonPageStep } from '..';
import fetch from 'cross-fetch';
import { isMobileOnly, useMobileOrientation } from 'react-device-detect';
import { Navigate, useNavigate } from 'react-router-dom';
import { DesktopView } from './desktop/Login.desktop';
import { MobileView } from './mobile/Login.mobile';
import apiHelper, {
  SupportedIncomingFormat,
  SupportedMethod,
  SupportedOutgoingFormat,
  SupportedServers,
} from '../../common/ApiHelper';
import { logoutBbl, resumeUserSession } from '../../common/CommonTools';
import { appConfig } from '../../common/ConfigLoader';
import { useAppSelector } from '../../store/hooks';
import { setAppInterfaceData } from '../../store/reducer/appInterfaceDataSlice';
import {
  selectAppAuthentication,
  selectAppPageStep,
  selectAppStateData,
  selectAppUserProfile,
  selectAppUserSubscription,
  setAppPageStep,
  updateAppUserProfile,
  updateAppUserSubscription,
} from '../../store/reducer/appStateSlice';
import { outsideComponentDispatch } from '../../store/store';
import { RotateScreen } from '../rotateScreen/rotateScreen';

export const Login = (): JSX.Element => {
  // Redux Hooks
  const pageStep = useAppSelector(selectAppPageStep);
  const authenticationData = useAppSelector(selectAppAuthentication);
  const userProfileData = useAppSelector(selectAppUserProfile);
  const userSubscriptionList = useAppSelector(selectAppUserSubscription);
  const appState = useAppSelector(selectAppStateData);
  const { isLandscape } = useMobileOrientation();

  // LOCAL HOOKS
  const [finishLoadingSubscription, setFinishLoadingSubscription] =
    React.useState(false);
  const [needSubscription, setNeedSubscription] =
    React.useState<boolean | undefined>(undefined);
  const [finishLoadingChildProfile, setFinishLoadingChildProfile] =
    React.useState(false);
  const [needChildProfile, setNeedChildProfile] =
    React.useState<boolean | undefined>(undefined);
  const [bblServerOnline, setBblServerOnline] =
    React.useState<boolean | undefined>(undefined);

  // Functions
  const loadUserProfile = async (): Promise<void> => {
    await apiHelper(
      SupportedServers.ACCOUNT,
      'user/UserProfile',
      SupportedMethod.POST,
      {},
      {
        incomingFormat: SupportedIncomingFormat.JSON,
        outgoingFormat: SupportedOutgoingFormat.JSON,
        headerConstructor() {
          return {
            authorization: authenticationData ? authenticationData.token : '',
          };
        },
      },
    ).then((res): void => {
      if (res.success && res.data) {
        outsideComponentDispatch(updateAppUserProfile(res.data));
        // SET FIRST CHILD AS DEFAULT IN THE BEGINNING
        outsideComponentDispatch(
          setAppInterfaceData({
            dataKey: 'childProfile',
            data: res.data?.childProfile.first,
          }),
        );

        if (
          res.data.parentProfile?.profile_data.accountAttributes
            ?.isInitialized ??
          true
        ) {
          // IF NO ACCOUNT ATTRIBUTES OR isInitialized IS UNDEFINED
          // REGARD IT AS OLD USER DATA WHICH HAS COMPLETE CHILD PROFILE DATA
          // SO NO NEED TO UPDATE CHILD PROFILE
          setNeedChildProfile(false);
        } else {
          setNeedChildProfile(true);
        }
      }

      setFinishLoadingChildProfile(true);
    });
  };

  const fetchUserSubscription = (): void => {
    const jsonData = {
      validityOption: 'VALID_ONLY',
    };

    void apiHelper(
      SupportedServers.ACCOUNT,
      'user/UserSubscriptionList',
      SupportedMethod.POST,
      jsonData,
      {
        outgoingFormat: SupportedOutgoingFormat.JSON,
        incomingFormat: SupportedIncomingFormat.JSON,
        headerConstructor() {
          return {
            authorization: authenticationData ? authenticationData.token : '',
          };
        },
      },
    ).then((result): void => {
      if (result.success) {
        // Login Successful
        outsideComponentDispatch(
          updateAppUserSubscription(result.data.data as any[]),
        );
        console.log('sub res', result);
        setNeedSubscription(
          result.data.data === undefined ? true : result.data.data.length === 0,
        );
        setFinishLoadingSubscription(true);
      }
    });
  };

  React.useEffect(() => {
    outsideComponentDispatch(setAppPageStep(CommonPageStep.LOGIN));

    // LOGIN PAGE WILL USE URL PARAMETER ONLY
    resumeUserSession();
  }, []);

  React.useEffect(() => {
    const loadUserAndFetchSubscription = async (): Promise<void> => {
      if (authenticationData) {
        await loadUserProfile();
        fetchUserSubscription();
      }
    };

    void loadUserAndFetchSubscription();
  }, [authenticationData]);

  const loginScreens = (): JSX.Element => {
    if (isMobileOnly) {
      if (isLandscape) {
        return <RotateScreen />;
      }

      return <MobileView />;
    }

    return <DesktopView />;
  };

  /* eslint-disable */
  return (
    <React.Fragment>
      {/* NO EXISTING SUBSCRIPTION PLAN */}
      {/* {finishLoadingSubscription && authenticationData && (userSubscriptionList === undefined || userSubscriptionList?.length === 0) ? ( */}
      {finishLoadingSubscription && needSubscription === true ? (
        <Navigate to={`/Subscription`} />
      ) : null}
      {/* EXISTING SUBSCRIPTION PLAN BUT NO CHILD PROFILE */}
      {/* {finishLoadingChildProfile && (needSubscription === false) && authenticationData &&
        userProfileData &&
        (!userProfileData?.parentProfile.profile_data.accountAttributes ||
          !userProfileData?.parentProfile.profile_data.accountAttributes
            ?.isInitialized) */}
      {finishLoadingChildProfile &&
      needSubscription === false &&
      needChildProfile === true ? (
        <Navigate to={`/ChildProfile`} />
      ) : null}
      {/* EXISTING SUBSCRIPTION PLAN, EXISTING CHILD PROFILE */}
      {finishLoadingSubscription &&
      finishLoadingChildProfile &&
      needSubscription === false &&
      needChildProfile === false &&
      authenticationData &&
      userProfileData?.childProfile.first ? (
        isMobileOnly ? (
          <Navigate to={`/ParentPortal`} />
        ) : (
          <Navigate to={`/BigBangLab`} />
        )
      ) : null}
      {pageStep === CommonPageStep.ENTRY ? <Navigate to={`/Entry`} /> : null}
      {pageStep === CommonPageStep.SIGNUP ? <Navigate to={`/SignUp`} /> : null}
      {pageStep === CommonPageStep.FORGETEMAIl ? (
        <Navigate to="/ForgetEmail" />
      ) : null}
      {pageStep === CommonPageStep.FORGETPASSWORD ? (
        <Navigate to="/ForgetPassword" />
      ) : null}
      {loginScreens()}
    </React.Fragment>
  );
};
